import { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { adminGet } from '../../lib/adminApi';

const stageLabels = {
  nouveau: 'Nouveau',
  visite: 'Visite',
  devis_envoye: 'Devis envoyé',
  accord_client: 'Accord client',
  facture: 'Facturé',
};

const notificationKindMap = {
  sig: 'crm-notification-item__icon crm-notification-item__icon--sig',
  new: 'crm-notification-item__icon crm-notification-item__icon--new',
  pay: 'crm-notification-item__icon crm-notification-item__icon--pay',
  warn: 'crm-notification-item__icon crm-notification-item__icon--warn',
};

const statusDotClass = {
  planifie: 'crm-status-dot crm-status-dot--gray',
  confirme: 'crm-status-dot crm-status-dot--green',
  termine: 'crm-status-dot crm-status-dot--green',
  annule: 'crm-status-dot crm-status-dot--orange',
};

const formatEuro = (value) =>
  `${Math.round(Number(value || 0)).toLocaleString('fr-FR')} €`;

const getTagClassName = (serviceType) => {
  if (serviceType === 'installation') return 'crm-tag crm-tag--installation';
  if (serviceType === 'sav') return 'crm-tag crm-tag--sav';
  if (serviceType === 'peinture') return 'crm-tag crm-tag--paint';
  if (serviceType === 'entretien') return 'crm-tag crm-tag--maintenance';
  return 'crm-tag crm-tag--repair';
};

const pipelineStages = ['nouveau', 'visite', 'devis_envoye', 'accord_client', 'facture'];

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({
    metrics: {
      signed_revenue: 0,
      active_leads: 0,
      conversion_rate: 0,
      pending_payments: 0,
    },
    pipeline: [],
    planning: [],
    notifications: [],
  });

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const data = await adminGet('/admin/crm/dashboard');
        setDashboard(data);
      } catch (error) {
        console.error('Error fetching CRM dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const metricCards = [
    {
      label: 'CA signé (mois)',
      value: formatEuro(dashboard.metrics.signed_revenue),
      subLabel: 'Suivi sur opportunités gagnées',
      subClassName: 'crm-kpi__sub crm-kpi__sub--up',
      accent: true,
    },
    {
      label: 'Leads actifs',
      value: String(dashboard.metrics.active_leads || 0),
      subLabel: 'Hors dossiers facturés',
      subClassName: 'crm-kpi__sub crm-kpi__sub--neutral',
    },
    {
      label: 'Taux transformation',
      value: `${dashboard.metrics.conversion_rate || 0} %`,
      subLabel: 'Pipeline commercial',
      subClassName: 'crm-kpi__sub crm-kpi__sub--up',
    },
    {
      label: 'En attente paiement',
      value: formatEuro(dashboard.metrics.pending_payments),
      subLabel: 'Factures à suivre',
      subClassName: 'crm-kpi__sub crm-kpi__sub--down',
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="crm-loading">
          <div className="crm-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <section className="crm-kpi-row" data-testid="admin-dashboard">
        {metricCards.map((metric) => (
          <article
            key={metric.label}
            className={`crm-kpi-card ${metric.accent ? 'is-accent' : ''}`}
          >
            <div className="crm-kpi__label">{metric.label}</div>
            <div className="crm-kpi__value">{metric.value}</div>
            <div className={metric.subClassName}>{metric.subLabel}</div>
          </article>
        ))}
      </section>

      <section className="crm-card">
        <div className="crm-section-head">
          <div>
            <h2 className="crm-section-title">Pipeline CRM</h2>
            <p className="crm-section-subtitle">
              Vue synthétique des opportunités réellement stockées en base.
            </p>
          </div>
        </div>

        <div className="crm-pipeline-grid">
          {pipelineStages.map((stage) => {
            const leads = dashboard.pipeline.filter((lead) => lead.stage === stage);
            return (
              <div key={stage} className="crm-pipeline-column">
                <div className="crm-pipeline-column__head">
                  <span>{stageLabels[stage]}</span>
                  <span className="crm-pill-count">{leads.length}</span>
                </div>

                <div className="crm-pipeline-column__body">
                  {leads.length === 0 ? (
                    <div className="crm-empty">Aucune opportunité</div>
                  ) : (
                    leads.slice(0, 4).map((lead) => (
                      <article key={lead.id} className="crm-pipeline-card">
                        <div className="crm-pipeline-card__name">{lead.company}</div>
                        <div className="crm-pipeline-card__type">
                          {lead.contact_name} · {lead.equipment || 'Engin non précisé'}
                        </div>
                        <span className={getTagClassName(lead.service_type)}>
                          {lead.service_type}
                        </span>
                        <div className="crm-pipeline-card__amount">
                          {formatEuro(lead.estimated_amount)}
                        </div>
                      </article>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="crm-bottom-grid">
        <article className="crm-card">
          <div className="crm-section-head">
            <div>
              <h2 className="crm-section-title">Planning du jour</h2>
              <p className="crm-section-subtitle">
                Interventions et visites enregistrées dans le module planning.
              </p>
            </div>
          </div>

          <div className="crm-stack-list">
            {dashboard.planning.length === 0 ? (
              <div className="crm-empty">Aucun évènement planifié.</div>
            ) : (
              dashboard.planning.map((item) => (
                <div key={item.id} className="crm-schedule-item">
                  <div className="crm-schedule-item__time">
                    {new Date(item.start_at).toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                  <div className="crm-schedule-item__body">
                    <div className="crm-schedule-item__client">{item.client}</div>
                    <div className="crm-schedule-item__tech">
                      {item.title} {item.technician_name ? `· ${item.technician_name}` : ''}
                    </div>
                  </div>
                  <div className={statusDotClass[item.status] || 'crm-status-dot crm-status-dot--gray'} />
                </div>
              ))
            )}
          </div>
        </article>

        <article className="crm-card">
          <div className="crm-section-head">
            <div>
              <h2 className="crm-section-title">Notifications</h2>
              <p className="crm-section-subtitle">
                Synthèse d’activité générée depuis les modules CRM.
              </p>
            </div>
          </div>

          <div className="crm-stack-list">
            {dashboard.notifications.map((notification) => (
              <div key={notification.id} className="crm-notification-item">
                <div className={notificationKindMap[notification.kind] || notificationKindMap.new}>
                  {notification.kind?.toUpperCase()}
                </div>
                <div>
                  <div className="crm-notification-item__text">{notification.text}</div>
                  <div className="crm-notification-item__time">{notification.time}</div>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>
    </AdminLayout>
  );
};

export default AdminDashboard;
